import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { emailLeadMagnet } from '../data/mock';
import { Download, Mail, CheckCircle2, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailCapture = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !name) {
      toast({
        title: "Missing Information",
        description: "Please enter your name and email address.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Submit to backend
      const response = await axios.post(`${BACKEND_URL}/api/leads`, {
        name,
        email,
        source: 'main-email-capture'
      });

      console.log('Lead captured:', response.data);
      
      setSubmitted(true);
      toast({
        title: "Success!",
        description: "Check your email for the free guide. It should arrive within 5 minutes.",
      });

    } catch (error) {
      console.error('Error capturing lead:', error);
      toast({
        title: "Oops!",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="email-capture" className="py-20 px-4 bg-gradient-to-br from-emerald-600 to-teal-600">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-3xl p-8 md:p-12 text-center shadow-2xl">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Check Your Email! 📧
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              We've sent the <strong>$10K Affiliate Blueprint</strong> to <strong>{email}</strong>
            </p>
            <p className="text-gray-600 mb-8">
              If you don't see it in a few minutes, check your spam folder. 
              Also, make sure to add us to your contacts so you don't miss future updates!
            </p>
            <Button 
              onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Browse Top Products
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="email-capture" className="py-20 px-4 bg-gradient-to-br from-emerald-600 to-teal-600 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-soft-light filter blur-3xl opacity-10"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full mix-blend-soft-light filter blur-3xl opacity-10"></div>
      </div>

      <div className="container mx-auto max-w-5xl relative z-10">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Copy */}
            <div>
              <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Sparkles className="w-4 h-4" />
                <span>FREE Download - Limited Time</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {emailLeadMagnet.title}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {emailLeadMagnet.subtitle}
              </p>

              {/* Benefits */}
              <div className="space-y-4 mb-8">
                {emailLeadMagnet.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Social Proof */}
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex -space-x-2">
                  {[
                    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80",
                    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80",
                    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80"
                  ].map((img, i) => (
                    <img key={i} src={img} alt="User" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                  ))}
                </div>
                <span><strong>2,847 people</strong> downloaded this week</span>
              </div>
            </div>

            {/* Right Side - Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-6 text-lg border-2 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Email Address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-6 text-lg border-2 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                    required
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-lg py-6 font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Free Guide
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  We respect your privacy. Unsubscribe at any time. No spam, guaranteed.
                </p>
              </form>

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-6 text-xs text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Mail className="w-4 h-4 text-emerald-600" />
                    <span>Instant Delivery</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>No Credit Card</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Sparkles className="w-4 h-4 text-emerald-600" />
                    <span>100% Free</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default EmailCapture;
